import React, {
    Fragment, useCallback, useEffect, useState,
  } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import {Grid, MenuItem} from '@material-ui/core/';
import Button from '@material-ui/core/Button';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import { useMutation, useQuery } from '@apollo/react-hooks';
import {
  Checkbox,
  FormControlLabel,
  Paper,
  TextField
} from '@material-ui/core';
  import PropertySearch from '../../../components/PropertySearch/PropertySearch';
  import { gql } from 'apollo-boost';
  import useStyles from './styles';
  import { useSnackbar } from 'notistack';
  import {
    beautifyErrors,
    transformGraphQlErrorMessage,
    ValidationErrors,
  } from '../../../utils/helpers';
  import { UPDATE_DEVICE, UPDATE_DEVICES } from '../../../graphql/queries/Devices'
  import CustomTextField from '../../../components/CustomTextField/CustomTextField';
  import CustomButton from '../../../components/CustomButton';
  import BuilderSearch from '../../../components/BulderSearch/BuilderSearch';
  import Builder from '../../../types/Builder';
  import InputDevice from '../../../types/inputTypes/Device';
  import { deviceCreateSchema, multiplyDeviceCreateSchema } from './DeviceValidation';
  import { BaseProperty as Property } from '../../../types/Property';
  import useProperty from '../../../hooks/useProperty';
  import HubTypeSelect from '../../../components/HubTypeSelect';
  import * as yup from 'yup';

  type Props = {
    loading?: boolean;
  } & RouteComponentProps;

  const DeviceForm: React.FC<Props> = props => {

    const {history} = props;
    const classes = useStyles();
    const [validationErrors, setValidationErrors] = useState<ValidationErrors<InputDevice>>({});
    const [device, setDevice] = useState<InputDevice>(new InputDevice());
    const [HubAccountId, setHubAccountId] = useState<{ id: string, username: string }[]>([]);
    const { property } = useProperty(device.property_id);
    const { enqueueSnackbar } = useSnackbar();
    const [multiplyDevices, setMultiplyDevices] = useState<InputDevice[]>([new InputDevice()]);
    const [validationMultiplyDeviceErrors, setValidationMultiplyDeviceErrors] = useState<ValidationErrors<InputDevice>[]>([]);

    const GET_HUBACCOUNT_IDS = gql`
    query {
      hubAccountIds {
        id,
        username
      }
    }
    `;

    const { data: hubAccountData } = useQuery<{ hubAccountIds: Array<{ id: string, username: string }> }>(
      GET_HUBACCOUNT_IDS,
    );

    useEffect(() => {
      if (hubAccountData) {
        setHubAccountId(hubAccountData.hubAccountIds);
      }
    }, [hubAccountData]);

    const handleDeviceChange = useCallback(
      ({ target: { name, value, checked, type } }: React.ChangeEvent<HTMLInputElement>) => {
        setDevice((prevDevice: InputDevice) => ({
          ...prevDevice,
          [name]: type === 'checkbox' ? checked : value,
        }));

        if (name === 'hub_type') {
          setMultiplyDevices([new InputDevice()]);
        }
      },
      [],
    );

    const handleDeviceSave = () => {
      try{
        const uDevice = deviceCreateSchema.validateSync(device, { abortEarly: false })
        updateDevice({
          variables: {
            device: {
              ...uDevice,
            },
      }}).then(() => history.goBack())
      .catch(err => {
        enqueueSnackbar(transformGraphQlErrorMessage(err.message), {
          variant: 'error',
        });
      });
    } catch (errors) {
      enqueueSnackbar('Error', { variant: 'error' });
      setValidationErrors(beautifyErrors<InputDevice>(errors));
    }
  }

    const handleBuilderChange = (builder: Builder) => {
      setDevice((device: InputDevice) => ({
        ...device,
        property_id: 0,
        builder_id: builder.builder_id,
      }));
    }

    const handlePropertyChange = (property: Property) => {
      setDevice((device: InputDevice) => ({
        ...device,
        builder_id: property.builder_id ?? 0,
        property_id: property.property_id,
      })
      )
    }

    const [updateDevice] = useMutation<{ updateDevice: InputDevice }>(
      UPDATE_DEVICE,
      { errorPolicy: 'all' }
    );

    const [updateMultipleHubs] = useMutation<{ updateDevices: InputDevice[] }>(
      UPDATE_DEVICES,
      { errorPolicy: 'all' },
    );

    const formatMacAddress = (input: string): string => {
      const hexOnly = input.replace(/[^a-fA-F0-9]/g, '').toUpperCase();

      const trimmed = hexOnly.substring(0, 12);

      const parts = trimmed.match(/.{1,2}/g);
      return parts ? parts.join(':') : '';
    };

    const handleMultiplyDevicesChange = (index: number, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target;
      setMultiplyDevices(prevDevices => {
        const updatedDevices = [...prevDevices];
        const newValue = name === 'mac' ? formatMacAddress(value) : value;
        updatedDevices[index] = {
          ...updatedDevices[index],
          [name]: newValue,
        };
        return updatedDevices;
      });
    };

    const handleMultiplyDevicesSave = () => {
      const errorsPerDevice: ValidationErrors<InputDevice>[] = [];
      let generalErrors: ValidationErrors<InputDevice> = {};
      let hasErrors = false;

      for (let i = 0; i < multiplyDevices.length; i++) {
        errorsPerDevice.push({});
      }

      try {
        deviceCreateSchema.validateSync(device, {
          abortEarly: false,
          context: {
            validateSerialNumber: false,
          },
        });
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          hasErrors = true;
          generalErrors = beautifyErrors<InputDevice>(error);
        }
      }

      try {
        multiplyDeviceCreateSchema.validateSync(multiplyDevices, { abortEarly: false });
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          hasErrors = true;
          error.inner.forEach((err: yup.ValidationError) => {
            const path = err.path || '';
            const match = path.match(/\[(\d+)\]\.(.+)/);
            if (match) {
              const index = parseInt(match[1], 10);
              const field = match[2] as keyof InputDevice;
              errorsPerDevice[index][field] = err.message;
            }
          });
        }
      }

      if (hasErrors) {
        enqueueSnackbar('Validation error', { variant: 'error' });
        setValidationErrors(generalErrors);
        setValidationMultiplyDeviceErrors(errorsPerDevice);
        return;
      }

      setValidationErrors({});
      setValidationMultiplyDeviceErrors([]);

      const preparedDevices = multiplyDevices.map(item => ({
        serial_number: item.serial_number,
        description: item.mac,
        imei: device.imei,
        builder_id: device.builder_id,
        property_id: device.property_id,
        hub_account_id: device.hub_account_id,
        hub_type: device.hub_type,
        comments: device.comments,
      }));

      updateMultipleHubs({
        variables: { devices: preparedDevices },
      })
        .then(() => history.goBack())
        .catch(err => {
          enqueueSnackbar(transformGraphQlErrorMessage(err.message), { variant: 'error' });
        });
    };

    const renderEzloFields = (item: InputDevice, index: number) => {
      const errors = validationMultiplyDeviceErrors[index] || {};
      return (
        <Fragment>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              id="serial_number"
              name="serial_number"
              label="Serial number"
              value={item.serial_number}
              onChange={e => handleMultiplyDevicesChange(index, e)}
              helperText={errors.serial_number || ''}
              error={!!errors.serial_number}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              id="mac"
              name="mac"
              label="MAC"
              value={item.mac}
              onChange={e => handleMultiplyDevicesChange(index, e)}
          helperText={errors.mac || ''}
          error={!!errors.mac}
        />
      </Grid>
        </Fragment>
      );
    }

    const renderEzloWifiFields = (item: InputDevice, index: number) => {
      //TODO: temporarily use ezlo fields until documentation for Ezlo Wifi is provided
      const errors = validationMultiplyDeviceErrors[index] || {};
      return (
        <Fragment>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              id="serial_number"
              name="serial_number"
              label="Serial number"
              value={item.serial_number}
              onChange={e => handleMultiplyDevicesChange(index, e)}
              helperText={errors.serial_number || ''}
              error={!!errors.serial_number}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              id="mac"
              name="mac"
              label="MAC"
              value={item.mac}
              onChange={e => handleMultiplyDevicesChange(index, e)}
          helperText={errors.mac || ''}
          error={!!errors.mac}
        />
      </Grid>
        </Fragment>
      );
    }

    const renderPeplinkFields = (item: InputDevice, index: number) => {
      //TODO: temporarily use ezlo fields until documentation for Peplink is provided
      const errors = validationMultiplyDeviceErrors[index] || {};
      return (
        <Fragment>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              id="serial_number"
              name="serial_number"
              label="Serial number"
              value={item.serial_number}
              onChange={e => handleMultiplyDevicesChange(index, e)}
              helperText={errors.serial_number || ''}
              error={!!errors.serial_number}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              margin="normal"
              id="mac"
              name="mac"
              label="MAC"
              value={item.mac}
              onChange={e => handleMultiplyDevicesChange(index, e)}
          helperText={errors.mac || ''}
          error={!!errors.mac}
        />
      </Grid>
        </Fragment>
      );
    }

    const renderSeamFields = () => {
      return (
        <Fragment>
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                margin="normal"
                id="serial_number"
                name="serial_number"
                label="Device Id"
                value={device.serial_number}
                onChange={handleDeviceChange}
                helperText={validationErrors.serial_number || ''}
              />
            </Grid>
        </Fragment>
      );
    }

    const renderADCFields = () => {
      return (
        <Fragment>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                id="serial_number"
                name="serial_number"
                label="Serial number"
                value={device.serial_number}
                onChange={handleDeviceChange}
                helperText={validationErrors.serial_number || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                id="adc_customer_id"
                name="adc_customer_id"
                label="ADC Customer ID"
                value={device.adc_customer_id}
                onChange={handleDeviceChange}
                helperText={validationErrors.adc_customer_id || ''}
              />
            </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              id="adc_device_id"
              name="adc_device_id"
              label="ADC Device Id"
              value={device.adc_device_id}
              onChange={handleDeviceChange}
              helperText={validationErrors.adc_device_id || ''}
              error={!!validationErrors.adc_device_id}
            />
          </Grid>
        </Fragment>
      );
    }

    const renderKwiksetFields = () => {
      return (
        <Fragment>
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                margin="normal"
                id="serial_number"
                name="serial_number"
                label="Serial number"
                value={device.serial_number}
                onChange={handleDeviceChange}
                helperText={validationErrors.serial_number || ''}
              />
            </Grid>
        </Fragment>
      );
    }

    const renderSchlageFields = () => {
      return (
        <Fragment>
          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              margin="normal"
              id="serial_number"
              name="serial_number"
              label="Serial Number"
              value={device.serial_number}
              onChange={handleDeviceChange}
              helperText={validationErrors.serial_number || ''}
            />
          </Grid>
        </Fragment>
      );
    }

    return(
      <Fragment>
      <div className={classes.actionsBar}>
        <div className={classes.actionButtonsWrapper}>
          <Button
            variant="contained"
            className={classes.backToButton}
            component={Link}
            to="/devices"
          >
            <ArrowLeftIcon />
            Devices
          </Button>
        </div>
      </div>
      <div className={classes.contentWrapper}>
        <Grid item xs={12} sm={12}>
          <Paper className={classes.paper}>
          <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <HubTypeSelect
              margin="normal"
              fullWidth
              id="hub_type"
              label="Hub Type"
              name="hub_type"
              select
              value={device.hub_type}
              error={!!validationErrors.hub_type}
              helperText={validationErrors.hub_type || ''}
              onChange={handleDeviceChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="normal"
                fullWidth
                select
                label="Hub Account"
                name="hub_account_id"
                onChange={handleDeviceChange}
                value={device.hub_account_id}
                helperText={validationErrors.hub_account_id}
                error={!!validationErrors.hub_account_id}
              >
                {HubAccountId.sort((a, b) => a.username.localeCompare(b.username)).map(HubAcc => (
                  <MenuItem
                    key={HubAcc.id}
                    value={HubAcc.id}
                  >
                    {HubAcc.username}
                  </MenuItem>
                ))}
             </TextField>
            </Grid>
            {(device.hub_type === 'Ezlo Secure') && multiplyDevices.map((value, index) => renderEzloFields(value, index))}
            {(device.hub_type === 'Ezlo Wifi') && multiplyDevices.map((value, index) => renderEzloWifiFields(value, index))}
            {(device.hub_type === 'Peplink') && multiplyDevices.map((value, index) => renderPeplinkFields(value, index))}
            {(device.hub_type === 'Seam') && renderSeamFields()}
            {(device.hub_type === 'ADC') && renderADCFields()}
            {(device.hub_type === 'Kwikset') && renderKwiksetFields()}
            {(device.hub_type === 'Schlage') && renderSchlageFields()}
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                margin="normal"
                id="imei"
                name="imei"
                label="IMEI"
                value={device.imei}
                onChange={handleDeviceChange}
                helperText={validationErrors.imei || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <BuilderSearch
                margin="normal"
                onBuilderChange={handleBuilderChange}
                label="Builder"
                helperText={validationErrors.builder_id}
                initialValue={device.builder_id ?? 0}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PropertySearch
                style={{ marginTop: '0' }}
                margin="normal"
                fullWidth
                //@ts-ignore
                initialValue={property?.property_id ? property : undefined}
                builderId={device.builder_id}
                onPropertyChange={handlePropertyChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                margin="normal"
                fullWidth
                multiline
                rows={4}
                rowsMax={8}
                label="Comments"
                name="comments"
                value={device.comments || ''}
                onChange={handleDeviceChange}
              />
              </Grid>
            {
              device.hub_type.toLowerCase() === 'adc' && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        color="secondary"
                        name="delay_animation"
                        checked={Boolean(device.delay_animation)}
                        onChange={handleDeviceChange}
                      />
                    )}
                    label="Delay Animation"
                    labelPlacement="end"
                  />
                </Grid>
              )
            }
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  margin="normal"
                  fullWidth
                  multiline
                  rows={4}
                  rowsMax={8}
                  label="Descriprion"
                  name="description"
                  value={device.description || ''}
                  onChange={handleDeviceChange}
                />
              </Grid>
            <Grid container spacing={4} justify="center" style={{ marginTop: '10px', marginBottom: '10px' }}>
                <Grid >
                    <CustomButton
                      variant="orange"
                      onClick={() => (
                        ['Ezlo Secure', 'Ezlo Wifi', 'Peplink'].includes(device.hub_type)
                          ? handleMultiplyDevicesSave()
                          : handleDeviceSave()
                      )}
                    >
                      Add
                    </CustomButton>
                </Grid>
              </Grid>
              </Grid>
          </Paper>
      </Grid>
      </div>
      </Fragment>
    )
  }

  export default React.memo(DeviceForm);
